import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import Icon, {
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, Form, message, Radio, Spin, Upload } from 'antd';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { UploadFile } from 'antd/es/upload/interface';
import { ReactComponent as excelSvg } from 'views/Setting/assets/excel.svg';
import { uploadExcel } from 'services/api/resource';
import useMarkResult from './MarkResult';

type NewMarkProps = {
  visible: boolean;
  onCancel: () => void;
  openResult: ReturnType<typeof useMarkResult>[0];
};

const templateDownloadMap: any = {
  // spuid
  1: {
    'zh-CN':
      'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/%E7%BC%BA%E8%B4%A7%E6%A0%87%E8%AE%B0%E4%B8%8A%E4%BC%A0%E6%A8%A1%E6%9D%BF-%E5%B9%B3%E5%8F%B0%E5%95%86%E5%93%81id.xls',
    'en-US':
      'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/Out%20of%20Stock%20Reminder%20Template-Marketplace%20Item%20ID.xls',
    id: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/Template%20Notifikasi%20Stok%20Kosong-Item%20ID%20Marketplace.xls',
    th: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/%E0%B9%81%E0%B8%9A%E0%B8%9A%E0%B8%9F%E0%B8%AD%E0%B8%A3%E0%B9%8C%E0%B8%A1%E0%B8%81%E0%B8%B2%E0%B8%A3%E0%B9%81%E0%B8%88%E0%B9%89%E0%B8%87%E0%B9%80%E0%B8%95%E0%B8%B7%E0%B8%AD%E0%B8%99%E0%B8%AA%E0%B8%B4%E0%B8%99%E0%B8%84%E0%B9%89%E0%B8%B2%E0%B8%AB%E0%B8%A1%E0%B8%94%E0%B8%82%E0%B8%AD%E0%B8%87-ID%20%E0%B8%AA%E0%B8%B4%E0%B8%99%E0%B8%84%E0%B9%89%E0%B8%B2%E0%B8%82%E0%B8%AD%E0%B8%87%E0%B9%81%E0%B8%9E%E0%B8%A5%E0%B8%95%E0%B8%9F%E0%B8%AD%E0%B8%A3%E0%B9%8C%E0%B8%A1.xls',
    tl: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/Out%20of%20Stock%20Reminder%20Template-Marketplace%20Item%20ID%28Tagalog%29.xls',
    vi: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/Ma%CC%82%CC%83u%20tho%CC%82ng%20ba%CC%81o%20he%CC%82%CC%81t%20ha%CC%80ng%20cu%CC%89a%20la%CC%80-ID%20sa%CC%89n%20pha%CC%82%CC%89m%20ne%CC%82%CC%80n%20ta%CC%89ng.xls',
    pt: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/Modelo%20de%20upload%20de%20lembrete%20fora%20de%20estoque%20do-ID%20do%20produto%20da%20plataforma.xls',
    es: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/spuid/Out%20of%20Stock%20Reminder%20Template-Marketplace%20Item%20ID.xls',
  },
  // skuid
  2: {
    'zh-CN':
      'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/%E7%BC%BA%E8%B4%A7%E6%A0%87%E8%AE%B0%E4%B8%8A%E4%BC%A0%E6%A8%A1%E6%9D%BF-%E5%B9%B3%E5%8F%B0SKU_id.xls',
    'en-US':
      'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/Out%20of%20Stock%20Reminder%20Template-Marketplace%20SKU%20ID.xls',
    id: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/Template%20Notifikasi%20Stok%20Kosong-SKU%20ID%20Marketplace.xls',
    th: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/%E0%B9%81%E0%B8%9A%E0%B8%9A%E0%B8%9F%E0%B8%AD%E0%B8%A3%E0%B9%8C%E0%B8%A1%E0%B8%81%E0%B8%B2%E0%B8%A3%E0%B9%81%E0%B8%88%E0%B9%89%E0%B8%87%E0%B9%80%E0%B8%95%E0%B8%B7%E0%B8%AD%E0%B8%99%E0%B8%AA%E0%B8%B4%E0%B8%99%E0%B8%84%E0%B9%89%E0%B8%B2%E0%B8%AB%E0%B8%A1%E0%B8%94%E0%B8%82%E0%B8%AD%E0%B8%87-SKU%20ID%20%E0%B8%82%E0%B8%AD%E0%B8%87%E0%B9%81%E0%B8%9E%E0%B8%A5%E0%B8%95%E0%B8%9F%E0%B8%AD%E0%B8%A3%E0%B9%8C%E0%B8%A1.xls',
    tl: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/Out%20of%20Stock%20Reminder%20Template-Marketplace%20SKU%20ID%28Tagalog%29.xls',
    vi: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/Ma%CC%82%CC%83u%20tho%CC%82ng%20ba%CC%81o%20he%CC%82%CC%81t%20ha%CC%80ng%20cu%CC%89a%20la%CC%80-SKU%20ID%20ne%CC%82%CC%80n%20ta%CC%89ng.xls',
    pt: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/Modelo%20de%20upload%20de%20lembrete%20fora%20de%20estoque%20do-SKU%20ID%20da%20plataforma.xls',
    es: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/skuid/Out%20of%20Stock%20Reminder%20Template-Marketplace%20SKU%20ID.xls',
  },
  // 商品货号
  3: {
    'zh-CN':
      'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/%E7%BC%BA%E8%B4%A7%E6%A0%87%E8%AE%B0%E4%B8%8A%E4%BC%A0%E6%A8%A1%E6%9D%BF-%E5%95%86%E5%93%81%E8%B4%A7%E5%8F%B7.xls',
    'en-US':
      'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/Out%20of%20Stock%20Reminder%20Template-Article%20No..xls',
    id: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/Template%20Notifikasi%20Stok%20Kosong-No.%20Artikel.xls',
    th: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/%E0%B9%81%E0%B8%9A%E0%B8%9A%E0%B8%9F%E0%B8%AD%E0%B8%A3%E0%B9%8C%E0%B8%A1%E0%B8%81%E0%B8%B2%E0%B8%A3%E0%B9%81%E0%B8%88%E0%B9%89%E0%B8%87%E0%B9%80%E0%B8%95%E0%B8%B7%E0%B8%AD%E0%B8%99%E0%B8%AA%E0%B8%B4%E0%B8%99%E0%B8%84%E0%B9%89%E0%B8%B2%E0%B8%AB%E0%B8%A1%E0%B8%94%E0%B8%82%E0%B8%AD%E0%B8%87-%E0%B8%AB%E0%B8%A1%E0%B8%B2%E0%B8%A2%E0%B9%80%E0%B8%A5%E0%B8%82%E0%B8%AA%E0%B8%B4%E0%B8%99%E0%B8%84%E0%B9%89%E0%B8%B2.xls',
    tl: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/Out%20of%20Stock%20Reminder%20Template-Article%20No.%28Tagalog%29.xls',
    vi: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/Ma%CC%82%CC%83u%20tho%CC%82ng%20ba%CC%81o%20he%CC%82%CC%81t%20ha%CC%80ng%20cu%CC%89a%20la%CC%80-Ma%CC%83%20ha%CC%80ng%20sa%CC%89n%20pha%CC%82%CC%89m.xls',
    pt: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/Modelo%20de%20upload%20de%20lembrete%20fora%20de%20estoque%20do-Nu%CC%81mero%20de%20produto.xls',
    es: 'https://clients.zhisuitech.com/fett-public/template/out-of-stock/itemno/Out%20of%20Stock%20Reminder%20Template-Article%20No..xls',
  },
};

const NewMark = memo((props: NewMarkProps) => {
  const { t } = useTranslate();
  const { visible, onCancel, openResult } = props;
  const [form] = Form.useForm();
  const [type, setType] = useState(1);
  const language = useSystemLanguageValue();
  const templateDownloadLink = templateDownloadMap?.[type]?.[language];
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadExcelLoading, setUploadExcelLoading] = useState(false);
  const excelUrlRef = useRef<string>('');

  const submit = useCallback(() => {
    form
      .validateFields()
      .then(() => {
        openResult({
          fileUrl: excelUrlRef.current,
          fileName: fileList[0].name,
          templateType: type,
        });
        onCancel();
      })
      .catch(() => {});
  }, [form, openResult, type, fileList, onCancel]);

  useEffect(() => {
    if (fileList.length === 0) {
      form.setFieldsValue({
        file: undefined,
      });
    } else {
      form.setFieldsValue({
        file: fileList[0].name,
      });
    }
  }, [fileList, form]);

  return (
    <JustRightHeightModal
      title={t('outofstock_new_mark')}
      visible={visible}
      onCancel={onCancel}
      centered
      width={600}
      maskClosable={false}
      className={css`
        .ant-modal-body {
          padding: 14px 24px 24px 16px !important;
          margin: 0 !important;
        }
      `}
      footer={
        <div className="flex justify-end">
          <Button onClick={onCancel}>{t('phrase.取消')}</Button>
          <Button loading={uploadExcelLoading} type="primary" onClick={submit}>
            {uploadExcelLoading ? t('WhatsApp.上传中') : t('phrase.确定')}
          </Button>
        </div>
      }
    >
      <div className="bg-[#f0f7ff] rounded-[4px] p-[8px]">
        <div className="flex items-center">
          <InfoCircleFilled style={{ color: '#336fff' }} />
          <div className="text-[#515359] ml-[7px]">{t('WhatsApp.温馨提示')}:</div>
        </div>
        <Ul>
          <div>{t('outofstock_upload_tip1')}</div>
          <div>{t('outofstock_upload_tip2_copy')}</div>
          <div>{t('outofstock_upload_tip3')}</div>
          <div>{t('outofstock_upload_tip4')}</div>
        </Ul>
      </div>
      <Form
        form={form}
        initialValues={{
          templateType: 1,
        }}
        onValuesChange={(changedValues) => {
          if (changedValues['templateType']) {
            setType(changedValues['templateType']);
          }
        }}
        layout="vertical"
        className={css`
          margin-top: 25px !important;
          .ant-form-item-label {
            color: #515359 !important;
            width: 100% !important;
            & > label {
              width: 100% !important;
            }
          }
          .ant-form-item-control-input {
            min-height: 20px !important;
          }
          .ant-form-item {
            margin-bottom: 16px !important;
          }
          .ant-upload {
            svg {
              width: 32px;
              height: 32px;
            }
            .desc {
              margin-top: 8px;
              color: #3370ff;
            }
            .limit {
              margin-top: 6px;
              font-size: 12px;
              color: #b5b9bf;
            }
          }
        `}
      >
        <Form.Item label={t('outofstock_upload_choose_type')} name="templateType" required>
          <Radio.Group>
            <Radio value={1}>{t('outofstock_table_spuid')}</Radio>
            <Radio value={2}>{t('outofstock_table_skuid')}</Radio>
            <Radio value={3}>{t('outofstock_table_sellerid')}</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={
            <div className="flex justify-between items-center w-full">
              <span>{t('outofstock_upload_title')}</span>
              <Button download type="link" href={templateDownloadLink} className="!pr-[0]">
                <DownloadOutlined />
                {t('aide.下载模版')}
              </Button>
            </div>
          }
          name="file"
          required
          rules={[
            {
              validateTrigger: ['onSubmit', 'onChange'],
              validator: () => {
                if (fileList.length === 0 || excelUrlRef.current === '') {
                  return Promise.reject(t('phrase.请上传文件'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Upload.Dragger
            accept=".xlsx,.xls"
            style={{ display: `${fileList.length === 0 ? 'block' : 'none'}` }}
            maxCount={1}
            showUploadList={false}
            fileList={fileList}
            beforeUpload={(file) => {
              const isSizeOk = file.size / 1024 / 1024 <= 10;
              if (!isSizeOk) {
                message.error(t('errorMsg.文件不可大于x', { x: ' 10 MB' }));
                return Upload.LIST_IGNORE;
              }
              if (file.name.indexOf('.xlsx') === -1 && file.name.indexOf('.xls') === -1) {
                message.error(t('ViewShops.限1个文件，文件拓展名为.xlsx .xls'));
                return Upload.LIST_IGNORE;
              }
              setFileList([file]);
              setUploadExcelLoading(true);
              uploadExcel(file)
                .then((res) => {
                  excelUrlRef.current = res.url;
                })
                .catch(() => {
                  message.error(t('phrase.上传失败'));
                  setFileList([]);
                })
                .finally(() => {
                  setUploadExcelLoading(false);
                });

              return false;
            }}
          >
            <Icon component={excelSvg} />
            <div className="desc">{t('ViewShops.点击或者将文件拖拽到此区域上传')}</div>
            <div className="limit">{t('ViewShops.限1个文件，文件拓展名为.xlsx .xls')}</div>
          </Upload.Dragger>
          {fileList.length > 0 && (
            <div className="px-[28px] py-[16px] flex items-center border border-dashed border-[#eee] h-[76px]">
              <div className="flex-shrink-0 mr-[8px]">
                {uploadExcelLoading ? (
                  <Spin
                    style={{ flexShrink: 0 }}
                    indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
                  />
                ) : (
                  <Icon style={{ flexShrink: 0, fontSize: 32 }} component={excelSvg} />
                )}
              </div>
              <div className="inline-flex items-center justify-between flex-grow">
                <div>{fileList[0].name}</div>
                <Button
                  className="mr-[-15px]"
                  type="text"
                  onClick={() => {
                    setFileList([]);
                    excelUrlRef.current = '';
                  }}
                >
                  <DeleteOutlined style={{ flexShrink: 0, fontSize: '16px' }} />
                </Button>
              </div>
            </div>
          )}
        </Form.Item>
      </Form>
    </JustRightHeightModal>
  );
});

const useNewMark = () => {
  const [visible, setVisible] = useState(false);
  const [openResult, instance2] = useMarkResult();
  const close = useCallback(() => {
    setVisible(false);
  }, []);
  const instance = (
    <>
      {instance2}
      {visible && <NewMark visible={visible} onCancel={close} openResult={openResult} />}
    </>
  );
  const open = useCallback(() => {
    setVisible(true);
  }, []);

  return [open, instance] as const;
};

const Ul = styled.div`
  padding: 0 22px;
  margin-top: 4px;
  & > div {
    font-size: 13px;
    line-height: 18px;
    color: #515359;
    padding-left: 10px;
    position: relative;
    &:before {
      display: block;
      content: '';
      position: absolute;
      width: 2px;
      height: 2px;
      border-radius: 2px;
      background: #515359;
      top: 9px;
      left: 0;
    }
  }
`;

export default useNewMark;
